import {
  ColumnDef,
  columnVisibilityFeature,
  createSortedRowModel,
  flexRender,
  RowData,
  rowSortingFeature,
  sortFns,
  SortingState,
  tableFeatures,
  useTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useState } from "react";
import { Trans } from "@lingui/react/macro";

// v9 requires features to be registered explicitly. `columnVisibilityFeature`
// backs `row.getVisibleCells()`; `rowSortingFeature` backs the sortable headers.
// `sortFns` must be registered too: columns default to `sortFn: "auto"`, and an
// unregistered comparator silently degrades to `sortFn_basic` (raw `<`/`>`).
const features = tableFeatures({
  columnVisibilityFeature,
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns,
});

/** Features registered for this table, for typing its column defs. */
export type ReportsTableFeatures = typeof features;

interface ReportsTableProps<TData extends RowData> {
  columns: ColumnDef<ReportsTableFeatures, TData>[];
  data: TData[];
}

export function ReportsTable<TData extends RowData>({
  columns,
  data,
}: ReportsTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useTable({
    features,
    data,
    columns,
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  return (
    <div
      className="overflow-hidden rounded-md border"
      data-testid="report-list"
    >
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 text-center text-muted-foreground"
              >
                <Trans>Inga rapporter skapade ännu</Trans>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
